import {
  hasCookieConsentForCategory,
  CookieName,
  CookieConsentCategory,
  FLOWSTEP_COOKIE_EXPIRATION_IN_DAYS,
  MixpanelEvent,
  PropertyDict,
} from "@flowstepdesign/shared";
import * as Sentry from "@sentry/react";
import Bowser from "bowser";
import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";

import { config } from "@/config";

/**
 * Tracks an event to be sent to Mixpanel.
 *
 * Automatically adds the following properties to the event:
 * - environment: The environment (e.g., development, production) of the application.
 * - site: The site (copilot) of the application.
 *
 * @param eventName - The name of the event to track.
 * @param properties - Optional object containing event properties.
 */
export const trackEvent = (
  eventName: MixpanelEvent,
  properties?: PropertyDict,
) => {
  if (!hasCookieConsentForCategory(CookieConsentCategory.enum.analytics))
    return;
  (async () => {
    try {
      await postAnalytics(eventName, {
        environment: config.env,
        site: "copilot",
        ...properties,
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          event_name: eventName,
          environment: config.env,
          site: "copilot",
          event_properties: properties,
        },
      });
    }
  })();
};

const postAnalytics = async (
  eventName: string,
  eventProperties?: PropertyDict,
) => {
  // Clone the event properties to avoid modifying the original object
  const clonedEventProperties: PropertyDict | undefined = eventProperties
    ? { ...eventProperties }
    : undefined;

  // Get additional properties for Mixpanel tracking
  const additionalProperties: PropertyDict = getAdditionalProperties(
    clonedEventProperties,
  );

  // Combine event properties with additional properties
  const properties: PropertyDict = {
    ...clonedEventProperties,
    ...additionalProperties,
  };
  // Call the analytics API route to send the event to Mixpanel
  await fetch(`${config.api.rest}/analytics`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      eventName: eventName,
      properties: properties,
    }),
  });
};

/**
 * Collects all additional properties for tracking.
 *
 * @param eventProperties - Optional object containing event properties.
 * @returns Object containing all additional tracking properties.
 */
const getAdditionalProperties = (
  eventProperties?: PropertyDict,
): PropertyDict => {
  // See https://docs.mixpanel.com/docs/tracking-methods/id-management/identifying-users-simplified#server-side-identity-management
  const deviceId: string = getDeviceId();
  const userId: string | undefined = getUserId(eventProperties);

  const userAgentProperties: PropertyDict = getUserAgentProperties();
  const referrerProperties: PropertyDict = getReferrerProperties();
  const utmProperties: PropertyDict = getUtmProperties();

  return {
    // Reserved event properties (see https://docs.mixpanel.com/docs/data-structure/property-reference/reserved-properties)
    // Default event properties (see https://docs.mixpanel.com/docs/data-structure/property-reference/default-properties)

    // User identity properties: $device_id (required) and $user_id (optional, with authenticated user)
    $device_id: deviceId,
    $user_id: userId,

    // Geolocation properties
    // See report.ts

    // User agent properties
    ...userAgentProperties,

    // Current URL
    $current_url: window.location.href,

    // Referrer properties
    ...referrerProperties,

    // UTM properties
    ...utmProperties,

    // Screen properties
    $screen_height: window.screen.height,
    $screen_width: window.screen.width,
  };
};

/**
 * Extracts the `user_id` identifier (`auth.users.id`) from event properties (if available) for tracking.
 * Also removes the `user_id` property from `eventProperties` after extraction.
 *
 * IMPORTANT: The `user_id` property is intentionally removed from `eventProperties` to avoid
 * sending it as a separate property to Mixpanel. This is because we use it as the `$user_id`
 * for identity management, not as a regular event property.
 *
 * @param eventProperties - Optional object containing event properties.
 * @returns The user identifier string if present in the properties, otherwise undefined.
 */
const getUserId = (
  eventProperties: PropertyDict | undefined,
): string | undefined => {
  if (!eventProperties) return undefined;

  const userId: string | undefined = eventProperties.user_id;

  // Remove the `user_id` property after extraction
  if ("user_id" in eventProperties) {
    delete eventProperties.user_id;
  }

  return userId;
};

/**
 * Retrieves Mixpanel device identifier (project-specific) from the cookie for tracking.
 * If the $device_id property already exists, it is returned. Otherwise, a new one is created with the format
 * "$device:{uuid}" and set in the cookie.
 *
 * @returns The device identifier string.
 */
const getDeviceId = (): string => {
  // Get Mixpanel data
  const mixpanelData: PropertyDict = getMixpanelData();

  // Get existing device identifier string, or undefined if it does not exist
  const deviceId: string | undefined = mixpanelData.$device_id;

  // Check if device identifier exists and is a string
  if (!deviceId || typeof deviceId !== "string") {
    // Generate new device identifier
    const newDeviceId: string = `$device:${uuidv4()}`;

    // Add new device identifier to Mixpanel data
    mixpanelData.$device_id = newDeviceId;

    // Set Mixpanel cookie
    Cookies.set(CookieName.FlowstepMixpanel, JSON.stringify(mixpanelData), {
      expires: FLOWSTEP_COOKIE_EXPIRATION_IN_DAYS,
      secure: true,
      domain: config.domain,
    });

    // Return new device identifier
    return newDeviceId;
  }

  // Return existing device identifier
  return deviceId;
};

/**
 * Retrieves Mixpanel data from the cookie.
 *
 * @returns Object containing Mixpanel data.
 */
const getMixpanelData = (): PropertyDict => {
  // Get Mixpanel cookie
  const mixpanelCookie: string | undefined = Cookies.get(
    CookieName.FlowstepMixpanel,
  );

  // Parse Mixpanel cookie with error handling
  let mixpanelData: PropertyDict = {};
  if (mixpanelCookie && typeof mixpanelCookie === "string") {
    try {
      mixpanelData = JSON.parse(mixpanelCookie);
    } catch {
      // Silently fail with empty object
    }
  }

  return mixpanelData;
};

/**
 * Retrieves all referrer properties for tracking, of which initial referrer and initial referring domain from the cookie.
 * If the $initial_referrer and $initial_referring_domain properties already exist, they are returned. Otherwise,
 * the current referrer and referring domain are collected and set in the cookie.
 *
 * @returns Object containing all referrer properties.
 */
const getReferrerProperties = (): PropertyDict => {
  // Get Mixpanel data
  const mixpanelData: PropertyDict = getMixpanelData();
  let updateCookie: boolean = false;

  // Handle initial referrer if missing
  if (
    !mixpanelData.$initial_referrer ||
    typeof mixpanelData.$initial_referrer !== "string"
  ) {
    mixpanelData.$initial_referrer = document.referrer || "$direct";
    updateCookie = true;
  }

  // Handle initial referring domain if missing
  if (
    !mixpanelData.$initial_referring_domain ||
    typeof mixpanelData.$initial_referring_domain !== "string"
  ) {
    mixpanelData.$initial_referring_domain = document.referrer
      ? new URL(document.referrer).hostname
      : "$direct";
    updateCookie = true;
  }

  // Update cookie only if needed
  if (updateCookie) {
    Cookies.set(CookieName.FlowstepMixpanel, JSON.stringify(mixpanelData), {
      expires: FLOWSTEP_COOKIE_EXPIRATION_IN_DAYS,
      secure: true,
      domain: config.domain,
    });
  }

  return {
    $initial_referrer: mixpanelData.$initial_referrer,
    $initial_referring_domain: mixpanelData.$initial_referring_domain,
    $referrer: document.referrer || undefined,
    $referring_domain: document.referrer
      ? new URL(document.referrer).hostname
      : undefined,
  };
};

/**
 * Extracts all user agent properties from the user agent string.
 *
 * @returns Object containing all user agent properties.
 */
const getUserAgentProperties = (): PropertyDict => {
  let $browser: string | undefined;
  let $browser_version: string | undefined;
  let $os: string | undefined;
  let $os_version: string | undefined;
  let $device: string | undefined;

  try {
    if (
      typeof window !== "undefined" &&
      window.navigator &&
      typeof window.navigator.userAgent === "string"
    ) {
      // See https://bowser-js.github.io/bowser/docs/global.html#ParsedResult
      const parser: Bowser.Parser.Parser = Bowser.getParser(
        window.navigator.userAgent,
      );
      $browser = parser.getBrowserName();
      $browser_version = parser.getBrowserVersion();
      $os = parser.getOSName();
      $os_version = parser.getOSVersion();
      $device = parser.getPlatformType();
    }
  } catch {
    // Silently fail with undefined values
  }

  return { $browser, $browser_version, $os, $os_version, $device };
};

/**
 * Extracts all UTM parameters from the current URL.
 *
 * @returns Object containing all UTM tracking properties.
 */
const getUtmProperties = (): PropertyDict => {
  // Extract all UTM parameters from the current URL
  const urlParams = new URLSearchParams(window.location.search);

  return {
    // Standard UTM parameters (widely used): https://ortto.com/learn/what-are-utm-parameters/
    utm_source: urlParams.get("utm_source") || undefined,
    utm_medium: urlParams.get("utm_medium") || undefined,
    utm_campaign: urlParams.get("utm_campaign") || undefined,
    utm_term: urlParams.get("utm_term") || undefined,
    utm_content: urlParams.get("utm_content") || undefined,

    // Extra UTM parameters (Mixpanel-specific): https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#track-utm-tags
    utm_id: urlParams.get("utm_id") || undefined, // Also used by Google Ads
    utm_source_platform: urlParams.get("utm_source_platform") || undefined,
    utm_campaign_id: urlParams.get("utm_campaign_id") || undefined,
    utm_creative_format: urlParams.get("utm_creative_format") || undefined,
    utm_marketing_tactic: urlParams.get("utm_marketing_tactic") || undefined,
  };
};
